/** @jsxImportSource @emotion/react */

import React from "react";
import Button from "@mui/material/Button";
import {alpha} from "@mui/material/styles";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTable, faTvRetro} from "@fortawesome/pro-regular-svg-icons";

import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import {GAMIFIED_ASPECT_RATIOS} from "js/dashboards/utils";
import currentClient from "js/common/repo/backbone/current-client";
import {clientPermissions} from "js/common/permissions";

export const AspectRatioCard = ({value, ratio, isSelected, onSelect, theme}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const previewWidth = 56; // slightly smaller so 4:3 description text has room
  const previewHeight = Math.round(previewWidth * ratio.h / ratio.w);

  const showHighlight = isSelected || isHovered;

  return (
      <div
          data-test-id={`aspect-ratio-card-${value}`}
          onClick={onSelect}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            width: 100,
            height: 159,
            border: isSelected
                ? `2px solid ${theme.palette.primary.main}`
                : `1px solid ${theme.palette.border.light}`,
            borderRadius: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            padding: "10px 5px",
            cursor: "pointer",
            flexShrink: 0,
            backgroundColor: showHighlight ? theme.palette.background.default : theme.palette.background.card,
            transition: "background-color 0.1s",
          }}
      >
        <div style={{height: 44, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0}}>
          <div style={{
            width: previewWidth,
            height: previewHeight,
            border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.text.lighter}`,
            borderRadius: 3,
            backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.3) : showHighlight ? theme.palette.border.light : theme.palette.background.default,
            flexShrink: 0,
          }} />
        </div>
        <div style={{fontSize: 14, fontWeight: 600, color: theme.palette.text.main, textAlign: "center", lineHeight: "18px"}}>
          {value}
        </div>
        <div style={{fontSize: 11, color: theme.palette.text.main, textAlign: "center", lineHeight: "15px", flex: 1}}>
          {ratio.description}
        </div>
      </div>
  );
};

const LayoutPreview = ({theme}) => (
    <div style={{
      width: 302,
      height: 159,
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.text.lighter}`,
      borderRadius: 5,
      padding: 16,
      display: "flex",
      flexDirection: "column",
      gap: 4,
      flexShrink: 0,
    }}>
      {[4, 2, 2].map((colCount, rowIdx) => (
          <div key={rowIdx} style={{display: "flex", gap: 4, flex: 1}}>
            {Array.from({length: colCount}).map((_, colIdx) => (
                <div key={colIdx} style={{
                  flex: 1,
                  backgroundColor: theme.palette.background.card,
                  border: `1px solid ${theme.palette.text.lighter}`,
                  borderRadius: 3,
                }} />
            ))}
          </div>
      ))}
    </div>
);

const CreateDashboardSelector = ({onCancel, onSelectClassic, onSelectGamified}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const [selectedAspectRatio, setSelectedAspectRatio] = React.useState("16:9");

  const hasGamifiedPermission = currentClient.hasPermission(clientPermissions.canUseGamifiedDashboards);

  const cardStyle = {
    flex: 1,
    maxWidth: 536,
    backgroundColor: theme.palette.background.card,
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: 5,
    padding: "36px 24px 16px",
    display: "flex",
    flexDirection: "column",
  };

  const sectionLabelStyle = {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.lighter,
    letterSpacing: "0.05em",
    marginBottom: 12,
    textTransform: "uppercase",
  };

  const sectionDividerStyle = {
    margin: "16px 0 12px",
    border: "none",
    borderTop: `1px solid ${theme.palette.border.light}`,
  };

  return (
      <div data-test-id="create-dashboard-selector" style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "40px 16px 32px",
        overflowY: "auto",
      }}>

        {/* Header */}
        <div style={{position: "relative", textAlign: "center", marginBottom: 32, width: "100%", maxWidth: 1200}}>
          <h2 style={{margin: "0 0 8px", fontSize: 24, fontWeight: 700, color: theme.palette.text.main, lineHeight: "36px"}}>
            Select Dashboard Type
          </h2>
          <p style={{margin: "0 auto 10px", fontSize: 16, color: theme.palette.text.main, lineHeight: "24px", maxWidth: 572}}>
            Choose the type of dashboard that best fits your needs. You can customize the layout and features after creation.
          </p>
        </div>

        {/* Cards */}
        <div style={{
          display: "flex",
          gap: 30,
          justifyContent: "center",
          width: "100%",
          maxWidth: 1200,
          borderRadius: 5,
        }}>

          {/* Standard Dashboard */}
          <div style={cardStyle}>
            <div style={{display: "flex", alignItems: "center", gap: 9, marginBottom: 16}}>
              <FontAwesomeIcon icon={faTable} style={{fontSize: 16, flexShrink: 0, color: theme.palette.error.main}} />
              <span style={{fontSize: 20, fontWeight: 600, color: theme.palette.text.main, lineHeight: "20px"}}>
                Standard Dashboard
              </span>
            </div>
            <p style={{margin: 0, fontSize: 14, color: theme.palette.text.main, lineHeight: "20px", height: 60}}>
              {"Create a flexible dashboard with "}
              <strong>{"unlimited panels"}</strong>
              {" and complete control over the layout. Perfect for comprehensive data visualization and monitoring."}
            </p>

            <hr style={sectionDividerStyle} />
            <div style={sectionLabelStyle}>Layout Preview</div>
            <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginBottom: 16}}>
              <LayoutPreview theme={theme} />
              <span style={{fontSize: 12, color: theme.palette.text.main, textAlign: "center"}}>
                Freely arrange panels in any configuration
              </span>
            </div>

            <div style={{display: "flex", justifyContent: "flex-end", marginTop: "auto", paddingTop: 8}}>
              <Button data-test-id="select-classic-dashboard" variant="contained" size="small" onClick={onSelectClassic}>
                Select
              </Button>
            </div>
          </div>

          {/* Gamified Dashboard */}
          {hasGamifiedPermission && (
              <div style={cardStyle}>
                <div style={{display: "flex", alignItems: "center", gap: 9, marginBottom: 16}}>
                  <FontAwesomeIcon icon={faTvRetro} style={{fontSize: 16, flexShrink: 0, color: theme.palette.error.main}} />
                  <span style={{fontSize: 20, fontWeight: 600, color: theme.palette.text.main, lineHeight: "20px"}}>
                    Gamified Dashboard
                  </span>
                </div>
                <p style={{margin: 0, fontSize: 14, color: theme.palette.text.main, lineHeight: "20px", height: 60}}>
                  {"Build an engaging dashboard with tabs for "}
                  <strong>{"storytelling and presentations"}</strong>
                  {". Ideal for team performance tracking and interactive reports."}
                </p>

                <hr style={sectionDividerStyle} />
                <div style={sectionLabelStyle}>Presentation Aspect Ratio</div>
                <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginBottom: 16}}>
                  <div style={{display: "flex", gap: 16}}>
                    {Object.entries(GAMIFIED_ASPECT_RATIOS).map(([value, ratio]) => (
                        <AspectRatioCard
                            key={value}
                            value={value}
                            ratio={ratio}
                            isSelected={selectedAspectRatio === value}
                            onSelect={() => setSelectedAspectRatio(value)}
                            theme={theme}
                        />
                    ))}
                  </div>
                  <span style={{fontSize: 12, color: theme.palette.text.main, textAlign: "center"}}>
                    Can be changed later in settings
                  </span>
                </div>

                <div style={{display: "flex", justifyContent: "flex-end", marginTop: "auto", paddingTop: 8}}>
                  <Button data-test-id="select-gamified-dashboard" variant="contained" size="small" onClick={() => onSelectGamified(selectedAspectRatio)}>
                    Select
                  </Button>
                </div>
              </div>
          )}
        </div>

        {/* Footer */}
        <div style={{marginTop: 32}}>
          <Button data-test-id="cancel-create-dashboard" variant="text" size="small" onClick={onCancel}>
            Cancel
          </Button>
        </div>

      </div>
  );
};

export default CreateDashboardSelector;
